import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowLeft, Sparkles, Crown, Building2, Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import { formatApiError } from "@/lib/api";

const PLANS = [
  {
    id: "free", name: "Free", Icon: Sparkles, accent: "slate",
    tagline: "Stay on top of daily life",
    features: [
      "Reminders & tasks — up to 10 active",
      "Morning Briefing — view only, 3 cards",
      "SnapBudget basic income & expense tracking",
      "Cash Miles mileage tracking",
      "Credit Score Tracker — manual entry & history",
    ],
    monthly: { price: "$0", suffix: "forever" }, annual: { price: "$0", suffix: "forever" },
    cta: "Get started", action: "free",
  },
  {
    id: "premium", name: "Premium", Icon: Crown, accent: "emerald", badge: "Most Popular",
    tagline: "Your full personal money workout",
    features: [
      "Everything in Free",
      "MoneyFlex full weekly workout — pay period planner & secondary (gig) income",
      "SnapBudget Budget vs Actual comparison",
      "Full Morning Briefing command center — all cards unlocked",
      "Tax Clarity Hub — personal deductions & tips",
      "Unlimited reminders & tasks",
    ],
    monthly: { price: "$29.99", suffix: "/mo", lookup: "premium_monthly", amt: 29.99 },
    annual: { price: "$299.90", suffix: "/yr", lookup: "premium_yearly", amt: 299.90, save: "2 months free" },
    action: "checkout",
  },
  {
    id: "platinum", name: "Business Platinum", Icon: Building2, accent: "amber", badge: "Best for Business",
    tagline: "Run the whole business",
    features: [
      "Everything in Premium",
      "Tax Clarity Hub — full IRS Schedule C business categories",
      "Business tab — full features",
      "Team boards, task assignment & overdue tracking",
      "SendGrid email notification flows",
      "Priority support badge",
    ],
    monthly: { price: "$69.99", suffix: "/mo", lookup: "platinum_monthly", amt: 69.99 },
    annual: { price: "$699.90", suffix: "/yr", lookup: "platinum_yearly", amt: 699.90, save: "2 months free" },
    action: "checkout",
  },
];

const ACCENTS = {
  slate: { ring: "border-white/10", chip: "text-slate-400", btn: "border border-white/15 text-slate-200 hover:bg-white/5", badge: "" },
  emerald: { ring: "border-emerald-500/50", chip: "text-emerald-300", btn: "bg-emerald-500 text-[#04140c] hover:bg-emerald-400", badge: "bg-emerald-500 text-[#04140c]" },
  amber: { ring: "border-amber-400/50", chip: "text-amber-300", btn: "bg-amber-400 text-[#1a1400] hover:bg-amber-300", badge: "bg-amber-400 text-[#1a1400]" },
};

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState("");
  const [period, setPeriod] = useState("monthly");

  const priceFor = (p) => (period === "annual" ? p.annual : p.monthly);

  const go = async (plan) => {
    if (plan.action === "free") return navigate(user ? "/" : "/login");
    if (!user) return navigate("/login");
    setBusy(plan.id);
    try {
      await startCheckout(priceFor(plan).lookup);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy("");
    }
  };

  const owned = (id) => {
    if (!user) return false;
    if (id === "platinum") return user.platinum_active;
    if (id === "premium") return user.premium_active && !user.platinum_active;
    return false;
  };

  const ctaLabel = (p) => {
    if (owned(p.id)) return "Active";
    if (p.id === "premium" && user?.platinum_active) return "Included in Platinum";
    if (p.cta) return p.cta;
    return period === "monthly" ? "Start 7-day free trial" : "Start trial · billed yearly";
  };

  return (
    <div className="min-h-screen bg-[#070d1c] text-slate-100" style={{ backgroundImage: "radial-gradient(700px circle at 50% -5%, rgba(16,185,129,0.12), transparent 60%)" }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <button data-testid="pricing-back" onClick={() => navigate(user ? "/" : "/login")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-300 transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mb-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-400">Plans & pricing</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-2">One plan for <span className="text-emerald-400">every stage</span></h1>
          <p className="text-slate-400 mt-3 max-w-2xl">Start free with reminders, Cash Miles and credit tracking. Upgrade to Premium for the full MoneyFlex workout, Budget vs Actual and Tax Clarity Hub, or Business Platinum for teams, Schedule C and business tools. Changes take effect immediately.</p>
        </div>

        <div className="inline-flex items-center gap-1 p-1 rounded-full border border-white/10 bg-[#0d1730] mb-8" data-testid="billing-toggle">
          <button data-testid="toggle-monthly" onClick={() => setPeriod("monthly")} className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${period === "monthly" ? "bg-white/10 text-white" : "text-slate-400 hover:text-slate-200"}`}>Monthly</button>
          <button data-testid="toggle-annual" onClick={() => setPeriod("annual")} className={`px-4 h-9 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${period === "annual" ? "bg-emerald-500 text-[#04140c]" : "text-slate-400 hover:text-slate-200"}`}>
            Annual <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${period === "annual" ? "bg-[#04140c]/20 text-[#04140c]" : "bg-emerald-500/15 text-emerald-300"}`}>2 months free</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch" data-testid="pricing-plans">
          {PLANS.map((p) => {
            const a = ACCENTS[p.accent];
            const pr = priceFor(p);
            const isOwned = owned(p.id);
            return (
              <div key={p.id} data-testid={`plan-${p.id}`} className={`relative bg-[#0d1730] border ${a.ring} rounded-2xl p-6 flex flex-col ${p.badge ? "ring-1 ring-white/10" : ""}`}>
                {p.badge && <span data-testid={`plan-${p.id}-badge`} className={`absolute -top-2.5 left-6 text-[10px] uppercase tracking-wider ${a.badge} font-bold px-2 py-0.5 rounded-full flex items-center gap-1`}><Star className="w-2.5 h-2.5 fill-current" /> {p.badge}</span>}
                <p.Icon className={`w-6 h-6 ${a.chip} mt-1`} />
                <h3 className="text-lg font-bold text-white mt-3">{p.name}</h3>
                <p className="text-xs text-slate-400 mt-1 min-h-[32px]">{p.tagline}</p>
                <div className="flex items-end gap-1 mt-4">
                  <span className="text-3xl font-bold text-white font-mono">{pr.price}</span>
                  <span className="text-slate-500 text-sm mb-1">{pr.suffix}</span>
                </div>
                {period === "annual" && pr.save && (() => {
                  const saved = (p.monthly.amt * 12 - pr.amt);
                  return <p className="text-[11px] text-emerald-400 mt-1">{pr.save} · save ${saved.toFixed(2)}/yr</p>;
                })()}
                {period === "monthly" && p.id !== "free" && <p className="text-[11px] text-emerald-400 mt-1">7-day free trial · save ${(p.monthly.amt * 12 - p.annual.amt).toFixed(2)}/yr with annual billing</p>}
                <ul className="mt-5 space-y-2 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-slate-300"><Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${a.chip}`} /> {f}</li>
                  ))}
                </ul>
                {isOwned ? (
                  <div data-testid={`plan-${p.id}-active`} className="mt-6 h-11 rounded-xl border border-emerald-500/40 text-emerald-300 text-sm font-medium flex items-center justify-center gap-2">
                    <Check className="w-4 h-4" /> Active
                  </div>
                ) : (
                  <button data-testid={`plan-${p.id}-cta`} onClick={() => go(p)} disabled={busy === p.id} className={`mt-6 h-11 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${a.btn}`}>
                    {busy === p.id ? "Redirecting…" : ctaLabel(p)}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-[11px] text-slate-600 mt-8">Secure checkout by Stripe. Payments are encrypted and protected.</p>
      </div>
    </div>
  );
}
